import { getCookie } from 'cookies-next';

import { CONSENT_COOKIE_KEY } from '@/utils/constants/common';

type ConsentCookiePurpose = { status?: string };
type ConsentCookiePayload = Partial<Record<string, ConsentCookiePurpose>>;

/**
 * CMP purpose backing each index of the consent-state array
 * [targeting, performance, functional, social] used by useConsentTracking.
 */
const PURPOSES_BY_CONSENT_STATE_INDEX = [
    'targeted_advertising',
    'analytics',
    'functional',
    'social_media',
] as const;

const GRANTED = 'granted';
const DENIED = 'denied';

/**
 * Reads the saved consent preferences from the CMP consent cookie
 * ({@link CONSENT_COOKIE_KEY} — base64-encoded JSON keyed by purpose, each with a
 * "granted"/"denied" status) and maps them to the consent-state array shape
 * `[targeting, performance, functional, social]`.
 *
 * Returns `null` when the cookie is absent, unreadable, or lacks a "granted"/"denied" status
 * for any of the four purposes, so callers can fall back to their own source. Before an explicit
 * choice in opt-in jurisdictions (e.g. GDPR), Ketch writes a `legalBasisDefault` cookie
 * that omits the opt-in purposes entirely — that is not a saved choice.
 */
export const getConsentStateFromCookie = (): boolean[] | null => {
    const rawCookie = getCookie(CONSENT_COOKIE_KEY);
    if (typeof rawCookie !== 'string' || !rawCookie) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(window.atob(rawCookie));
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return null;
        }

        const purposes = parsed as ConsentCookiePayload;
        const statuses = PURPOSES_BY_CONSENT_STATE_INDEX.map(
            (purpose) => purposes[purpose]?.status,
        );
        if (!statuses.every((status) => status === GRANTED || status === DENIED)) {
            return null;
        }
        return statuses.map((status) => status === GRANTED);
    } catch {
        return null;
    }
};
